import React, { useState } from 'react';

import { cloneDeep } from 'lodash';
import { ROUTES } from 'src/constants/routes';

export interface SaveState {
    /**
     * True if there is an ongoing "save" operation like an API call.
     */
    readonly isSaving: boolean;

    /**
     * List of Step IDs for which the manager has recorded unsaved data.
     */
    readonly unsavedChanges: string[];

    /**
     * Notifies the manager that there are updates for a given step.
     * @param step The step ID which has updates.
     */
    readonly registerChange: (step: string) => void;

    /**
     * Saves updates for the given steps and return a list of error messages
     * which were generated by the save.
     *
     * If the manager is not tracking any updates for the given steps, do nothing.
     * If steps are not given, save all updates the manager is currently tracking.
     * @param steps The steps to save updates for. Defaults to "all steps".
     */
    readonly saveChanges: (
        steps1: string[] | undefined,
        isPublish: boolean | undefined,
        fullSteps: string[] | undefined
    ) => Promise<string[]>;
}

interface SaveResult {
    readonly stepId: string;

    /**
     * Empty if there were no errors while saving the step's data.
     */
    readonly errorMessage: string;
}

export type SaveFunc<T> = (props: T) => Promise<string>;
export type Handlers<T> = { [name: string]: SaveFunc<T> };

async function saveStepData<T>(step: string, props: T, handlers: Handlers<T>): Promise<string> {
    if (step in handlers) {
        return handlers[step](props);
    }
    return 'Unknown step: ' + step;
}

async function saveRateCardValues<T>(steps: string[], props: T, handlers: Handlers<T>): Promise<SaveResult[]> {
    const saveResultPromises: Promise<SaveResult>[] = [];

    for (const stepId of steps) {
        const saveResultPerStep = await saveStepData(stepId, props, handlers).then((errorMessage) => ({
            stepId,
            errorMessage
        }));

        saveResultPromises.push(
            new Promise((resolve) => {
                resolve(saveResultPerStep);
            })
        );
    }

    return Promise.all(saveResultPromises);
}

/**
 * Use React state to track and save edits. Returns callbacks for
 * registering edits and saving state via backend APIs.
 */
function trackRateCardState<T>(props: T, handlers: Handlers<T>): SaveState {
    const [isSaving, setSaving] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState<string[]>([]);

    const registerChange = (action: string) => {
        setUnsavedChanges((existing) => (existing.includes(action) ? existing : [action, ...existing]));
    };

    const saveChanges = async (steps?: string[], isPublish?: boolean, fullStepList?: string[]) => {
        const toSave = steps ?? unsavedChanges;

        let included = unsavedChanges.filter((id) => toSave.includes(id));
        let excluded = unsavedChanges.filter((id) => !toSave.includes(id));

        if (steps?.includes(ROUTES.RATE_CARDS.STEP.REVIEW)) {
            included = cloneDeep(fullStepList) ?? included;
            excluded = [];
        }

        if (isPublish) {
            included.push(ROUTES.RATE_CARDS.STEP.REVIEW);
        }

        if (included.length > 0) {
            setSaving(true);
            const results = await saveRateCardValues(included, props, handlers);
            const failed = results.filter(({ errorMessage }) => errorMessage.length > 0);
            const failedSteps = failed.map(({ stepId }) => stepId);
            const errorMessages = failed.map(({ errorMessage }) => errorMessage);

            // if a step failed to save, it should still be tracked as "unsaved"
            setUnsavedChanges([...excluded, ...failedSteps]);
            setSaving(false);
            return errorMessages;
        } else {
            return [];
        }
    };

    return { saveChanges, registerChange, isSaving, unsavedChanges };
}

export const SaveManager = { trackRateCardState };
